import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { AlertCircle, Loader2 } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

interface PdfDocumentPreviewProps {
  fileUrl: string;
  fileName: string;
}

export const PdfDocumentPreview = ({ fileUrl, fileName }: PdfDocumentPreviewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageWidth, setPageWidth] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => setPageWidth(Math.max(0, container.clientWidth - 32));
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setPageCount(0);
    setError(null);
  }, [fileUrl]);

  return (
    <div ref={containerRef} className="min-h-full w-full bg-muted/40 p-4">
      <Document
        file={fileUrl}
        loading={
          <div className="flex min-h-[50vh] items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading document…
          </div>
        }
        error={
          <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p>{error || "This PDF could not be displayed."}</p>
          </div>
        }
        onLoadSuccess={({ numPages }) => {
          setPageCount(numPages);
          setError(null);
        }}
        onLoadError={(loadError) => setError(loadError.message)}
        className="flex flex-col items-center gap-4"
      >
        {pageWidth > 0 &&
          Array.from({ length: pageCount }, (_, index) => (
            <figure key={index} className="m-0 max-w-full">
              <Page
                pageNumber={index + 1}
                width={pageWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading={
                  <div
                    className="flex items-center justify-center bg-background text-sm text-muted-foreground shadow-sm"
                    style={{ width: pageWidth, minHeight: Math.min(pageWidth * 1.3, 720) }}
                  >
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading page {index + 1}…
                  </div>
                }
              />
              <figcaption className="sr-only">
                {fileName}, page {index + 1} of {pageCount}
              </figcaption>
            </figure>
          ))}
      </Document>
    </div>
  );
};